import type { ExtendedWord, Sticker } from '../types';

/**
 * Convert english word to image filename format
 * Simply lowercases the word - CSV already uses hyphens for multi-word entries
 */
function englishWordToImageName(englishWord: string): string {
  return englishWord.toLowerCase();
}

/**
 * Get the base image name for a word (without extension)
 * Uses englishWord from CSV, with fallback to transliteration
 */
export function getWordImageBaseName(word: ExtendedWord): string {
  // If word has custom image field, use it (strip extension if present)
  if (word.image) {
    return word.image.replace(/\.(png|svg|jpg|webp)$/i, '');
  }
  
  // Use englishWord from CSV if available
  if (word.englishWord) {
    return englishWordToImageName(word.englishWord);
  }
  
  // Fallback to lowercase transliteration
  return word.transliteration.toLowerCase();
}

/**
 * Get the image filename for a word (with extension)
 * Prefers PNG over SVG for better quality
 */
export function getWordImageName(word: ExtendedWord): string {
  const baseName = getWordImageBaseName(word);
  // Default to PNG (generated by Imagen), fallback handled in component
  return `${baseName}.png`;
}

/**
 * Get the full image URL for a word
 * Uses /images/items/ directory where generated images are stored
 */
export function getWordImageUrl(word: ExtendedWord): string {
  return `/images/items/${getWordImageName(word)}`;
}

/**
 * Get fallback image URL (SVG version) for a word
 */
export function getWordImageFallbackUrl(word: ExtendedWord): string {
  const baseName = getWordImageBaseName(word);
  return `/images/items/${baseName}.svg`;
}

/**
 * Placeholder SVG for missing images
 */
export const PLACEHOLDER_IMAGE = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="40" fill="%23e5e7eb"/%3E%3Ctext x="50" y="55" font-size="20" text-anchor="middle" fill="%23666"%3E%3F%3C/text%3E%3C/svg%3E';

/**
 * Get the image URL for a sticker.
 * Uses 320px thumbnails for on-screen display and full 1024px originals for print.
 *
 * @param sticker - The sticker object
 * @param variant - 'thumb' for on-screen (default), 'full' for print
 */
export function getStickerImageUrl(sticker: Sticker, variant: 'thumb' | 'full' = 'thumb'): string {
  if (variant === 'full') {
    return `/images/stickers/${sticker.image}`;
  }
  return `/images/stickers/thumb/${sticker.image}`;
}
